import React, { useContext, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton } from "src/components/GlassButtons";
import { AnnotationType, AnnotationContextType, type ANNAnnotationRecord, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { uploadOverlayImage } from "./ANNImageOverlayStorage";
import { useDeleteAnnotation, useSaveAnnotation } from "./ANNState";
import { ANNManager } from "./ANNManager";
import {
   buildOverwriteServitutTextDialogMessage,
   findAllServitutDocumentAnnotations,
   mergeDocumentPayloadWithLlmAnalysis,
   normalizeAnnotationTypeValue,
} from "./ANNServitutDocumentAnnotations";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
interface ANNCreateDocumentProps {
   open: boolean;
   contextType: AnnotationContextType;
   documentId?: string;
   bfeNumber?: string | number;
   datoloebenr?: string;
   onClose: () => void;
   onCreated?: (annotation: ANNAnnotationRecord) => void;
   editAnnotation?: ANNAnnotationRecord;
   oldAnnotationId?: string;
}
export function ANNCreateDocument(props: ANNCreateDocumentProps) {
   const { open, contextType, documentId, bfeNumber, datoloebenr, onClose, onCreated, editAnnotation } = props;
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const saveAnnotation = useSaveAnnotation();
   const deleteAnnotation = useDeleteAnnotation();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const organizationId = sessionState?.customerRef;
   const [title, setTitle] = useState(editAnnotation?.title || "");
   const [file, setFile] = useState<File | null>(null);
   const [annotationType, setAnnotationType] = useState<AnnotationTypeCategory>(editAnnotation?.annotationType || "Dokument");
   const [quality, setQuality] = useState<AnnotationQuality | undefined>(editAnnotation?.quality);
   const [verifyStamp, setVerifyStamp] = useState(editAnnotation?.verified || false);
   const [historyComment, setHistoryComment] = useState("");
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);

   useEffect(() => {
      if (editAnnotation) {
         setTitle(editAnnotation.title || "");
         setAnnotationType(editAnnotation.annotationType || "Dokument");
         setQuality(editAnnotation.quality);
         setVerifyStamp(editAnnotation.verified || false);
      } else {
         setTitle("");
         setAnnotationType("Dokument");
         setQuality(undefined);
         setVerifyStamp(false);
      }
      setHistoryComment("");
   }, [editAnnotation]);

   if (!open) return null;
   const handleCreate = async () => {
      if (contextType === AnnotationContextType.Servitut && !documentId) {
         setError(Localization.getText("No document selected"));
         return;
      }
      if (contextType === AnnotationContextType.Ejendom && !bfeNumber) {
         setError(Localization.getText("No BFE number available"));
         return;
      }
      if (!organizationId) {
         setError(Localization.getText("No organization selected"));
         return;
      }
      if (!title.trim()) {
         setError(Localization.getText("Title is required"));
         return;
      }
      if (!editAnnotation && !file) {
         setError(Localization.getText("No file selected"));
         return;
      }

      const normalizedAnnotationType = normalizeAnnotationTypeValue(annotationType);
      const isPdfFile = Boolean(
         file && (file.type.includes("pdf") || file.name.toLowerCase().endsWith(".pdf")),
      );
      const isNewServitutTextDocument =
         !editAnnotation
         && contextType === AnnotationContextType.Servitut
         && Boolean(documentId)
         && (normalizedAnnotationType === "dokument" || normalizedAnnotationType === "servitutdokument")
         && isPdfFile;

      const persistDocument = async (
         overwriteTarget?: ANNAnnotationRecord,
         duplicatesToRemove: ANNAnnotationRecord[] = [],
      ) => {
         setSaving(true);
         setError(null);
         try {
            let documentData = editAnnotation?.document;
            if (!editAnnotation && file) {
               const uploadResult = await uploadOverlayImage(file);
               documentData = {
                  filename: file.name,
                  s3Key: uploadResult.key,
                  s3Url: uploadResult.url,
                  mimeType: file.type,
                  size: file.size,
               };
            }

            if (!documentData) {
               setError(Localization.getText("No document data available"));
               return;
            }

            const record = await saveAnnotation({
               contextType,
               documentId,
               bfeNumber,
               datoloebenr,
               organizationId,
               type: AnnotationType.Document,
               annotationType,
               quality,
               verified: canVerify && verifyStamp,
               title: title.trim(),
               historyComment: historyComment.trim() || undefined,
               document: mergeDocumentPayloadWithLlmAnalysis(
                  documentData,
                  undefined,
                  overwriteTarget?.document || editAnnotation?.document,
               ),
               userId: sessionState?.userRef || "anonymous",
               userName: sessionState?.userName || sessionState?.userRef || "anonymous",
               id: overwriteTarget?.id || editAnnotation?.id,
               originalCreatedAt: overwriteTarget?.createdAt || editAnnotation?.createdAt,
               originalCreatedBy: overwriteTarget?.createdBy || editAnnotation?.createdBy,
               originalCreatedByName: overwriteTarget?.createdByName || editAnnotation?.createdByName,
            });

            for (const duplicate of duplicatesToRemove) {
               if (!duplicate.id || duplicate.id === overwriteTarget?.id) {
                  continue;
               }
               try {
                  await deleteAnnotation(duplicate.id, organizationId);
               } catch (deleteErr) {
                  console.warn("Failed to delete duplicate servitut text annotation", deleteErr);
               }
            }

            onCreated?.(record);
            setTitle("");
            setFile(null);
            setQuality(undefined);
            setVerifyStamp(false);
            setHistoryComment("");
            onClose();
         } catch (err: any) {
            setError(err?.message || String(err));
         } finally {
            setSaving(false);
         }
      };

      if (isNewServitutTextDocument && documentId && organizationId) {
         try {
            const existingItems = await ANNManager.listAnnotations(
               AnnotationContextType.Servitut,
               documentId,
               organizationId,
            );
            const existingServitutTexts = findAllServitutDocumentAnnotations(existingItems, datoloebenr);
            const overwriteTarget = existingServitutTexts[0];
            if (overwriteTarget) {
               const dialog = buildOverwriteServitutTextDialogMessage(overwriteTarget);
               windowDispatch(openWindow(WindowId.ConfirmationDialog, {
                  header: dialog.header,
                  children: dialog.children,
                  onSubmit: () => {
                     void persistDocument(overwriteTarget, existingServitutTexts.slice(1));
                  },
               }));
               return;
            }
         } catch (err: any) {
            setError(err?.message || String(err));
            return;
         }
      }

      await persistDocument();
   };
   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={editAnnotation ? Localization.getText("Edit document") : Localization.getText("Create document")}
         allowFullscreen
         allowUserMiniMize
         PosDefault={{ left: "25%", top: "15%", width: "520px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <div className="ann-create-form-container">
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Annotation Type")}
                  </label>
                  <select
                     value={annotationType}
                     onChange={e => setAnnotationType(e.target.value as AnnotationTypeCategory)}
                     className="ann-create-select"
                  >
                     {ANNOTATION_TYPES.map(type => (
                        <option key={type} value={type}>{Localization.getText(type)}</option>
                     ))}
                  </select>
               </div>
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Quality Assessment")}
                  </label>
                  <div className="ann-create-quality-selector-group">
                     <div
                        onClick={() => setQuality(undefined)}
                        className={`ann-create-quality-selector ${quality === undefined ? "ann-create-quality-selector-none ann-create-quality-selector-selected" : ""}`}
                     >
                        <span className="ann-create-quality-label">{Localization.getText("None")}</span>
                     </div>
                     {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                        const isSelected = quality === q;
                        const label = q === AnnotationQuality.Gold ? Localization.getText("Gold") : q === AnnotationQuality.Silver ? Localization.getText("Silver") : Localization.getText("Bronze");
                        return (
                           <div
                              key={q}
                              onClick={() => setQuality(q)}
                              className={`ann-create-quality-selector ${isSelected ? "ann-create-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                           >
                              <div className={`ann-create-quality-indicator-circle ${isSelected ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                              <span className={`ann-create-quality-label ${isSelected ? "ann-create-quality-label-selected" : ""}`}>{label}</span>
                           </div>
                        );
                     })}
                  </div>
               </div>
               {canVerify && (
                  <div>
                     <label
                        className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                     >
                        <input
                           type="checkbox"
                           checked={verifyStamp}
                           onChange={(e) => setVerifyStamp(e.target.checked)}
                           className="ann-create-verifyCheckbox"
                        />
                        <span style={{ fontSize: "20px" }} className="ann-create-verifyIcon">
                           <MIBIcon icon="q_userVerified" />
                        </span>
                        <span className="ann-create-verifyText">
                           {Localization.getText("Stamp this annotation as verified")}
                        </span>
                     </label>
                  </div>
               )}
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Title")}
                  </label>
                  <input
                     value={title}
                     onChange={(e) => setTitle(e.target.value)}
                     className="ann-create-input"
                  />
               </div>
               {!editAnnotation && (
                  <div>
                     <label className="ann-create-label">
                        {Localization.getText("File")}
                     </label>
                     <input
                        type="file"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        className="ann-create-input"
                     />
                     {file && (
                        <div className="ann-create-file-info">
                           {file.name} ({Math.round(file.size / 1024)} KB)
                        </div>
                     )}
                  </div>
               )}
               {editAnnotation && editAnnotation.document && (
                  <div>
                     <label className="ann-create-label">
                        {Localization.getText("File")}
                     </label>
                     <div className="ann-create-file-info">
                        {editAnnotation.document.filename || "Document"} {editAnnotation.document.size ? `(${Math.round(editAnnotation.document.size / 1024)} KB)` : ""}
                     </div>
                  </div>
               )}
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("History comment")}
                  </label>
                  <textarea
                     rows={3}
                     value={historyComment}
                     onChange={(e) => setHistoryComment(e.target.value)}
                     className="ann-create-textarea"
                     placeholder={Localization.getText("Optional history comment")}
                  />
               </div>
               {error && (
                  <div className="ann-create-error">{error}</div>
               )}
               <div className="ann-create-button-group">
                  <GlassButton disabled={saving} onClick={handleCreate}>
                     {saving ? Localization.getText("Saving...") : (editAnnotation ? Localization.getText("Save") : Localization.getText("Create"))}
                  </GlassButton>
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}
